import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography, OutlinedInput } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';
import { setItemCategories } from '../../../store/itemCategoriesSlice';

/** Custom Utils. */
import { MESSAGES, validateCategoryName, isDuplicateName, showSnackbar } from '../../../lib/configUtils';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import ThemeButton from 'shared/ui/Button';
import CustomSkeleton from 'shared/ui/Skeleton';

export default function ItemCategories({ userEmail, getAccessTokenSilently, loadingState, setLoadingState }) {
    const dispatch = useDispatch();
    /** State for item categories. */
    const categories = useSelector((state) => state.itemCategories);

    /** State for category rows. */
    const [categoryRows, setCategoryRows] = useState([]);
    /** State for category name. */
    const [categoryName, setCategoryName] = useState('');

    /** Function to get the columns for the DataGrid. */
    const getColumns = () => {
        return [
            { field: 'id', headerName: 'ID', flex: 0.5 },
            { field: 'category_name', headerName: 'Name', flex: 2, editable: true },
        ];
    };

    /** Handler for add category button click. */
    const handleAddCategory = () => {
        const trimmedName = categoryName.trim();

        if (!validateCategoryName(trimmedName)) {
            showSnackbar(dispatch, MESSAGES.EMPTY_NAME, 'warning');
            return;
        }

        if (isDuplicateName(categoryRows, trimmedName, 'category_name')) {
            showSnackbar(dispatch, MESSAGES.DUPLICATE_CATEGORIES, 'warning');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to add the category "${trimmedName}"?`);
        if (!confirmed) return;

        const payload = { id: randomId(), category_name: categoryName };

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_item_category',
            getAccessTokenSilently,
            email: userEmail,
            obj: payload,
            onInit: () => setLoadingState((prev) => ({ ...prev, circularLoading: true })),
            onFinal: () => setLoadingState((prev) => ({ ...prev, circularLoading: false })),
            onSuccess: (resp) => {
                console.log('resp', resp);
                setCategoryName('');
                dispatch((d, getState) => {
                    const prev = getState().itemCategories;
                    d(setItemCategories([...prev, resp.data]));
                });
                showSnackbar(dispatch, MESSAGES.ADD_SUCCESS, 'success');
            },
            onError: (error) => {
                showSnackbar(dispatch, MESSAGES.ADD_ERROR, 'error');
            },
        });
    };

    /** Effect to set the category rows when the categories change. */
    useEffect(() => {
        if (loadingState.itemCategories) return;
        setCategoryRows(categories);
    }, [categories, loadingState.itemCategories]);

    /** Skeleton loading state. */
    if (loadingState.itemCategories) {
        return (
            <Box sx={{ width: '500px', height: 'auto', p: 2 }}>
                <CustomSkeleton />
            </Box>
        );
    }
    console.log('categoryRows', categoryRows);
    return (
        <Box sx={{ width: '500px', height: 'auto', p: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.secondary' }}>Item Categories</Typography>
            <Box sx={{ my: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <OutlinedInput
                    variant="filled"
                    size="small"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    sx={{
                        width: '300px',
                        fontSize: '13px',
                        height: '30px',
                        '& .MuiInputBase-input': { height: '27px' },
                    }}
                />
                <ThemeButton text="Add" onClick={handleAddCategory} />
            </Box>
            <DataGridPremium
                rows={categoryRows}
                columns={getColumns()}
                density="compact"
                disableRowSelectionOnClick
                unstable_cellSelection
                disableColumnMenu
                disableColumnSorting
                processRowUpdate={async (newRow, oldRow) => {
                    try {
                        await new Promise((resolve, reject) => {
                            const trimmedName = newRow.category_name.trim();
                            const filteredRows = categoryRows.filter((row) => row.id !== oldRow.id);
                            if (isDuplicateName(filteredRows, trimmedName, 'category_name')) {
                                showSnackbar(dispatch, MESSAGES.DUPLICATE_CATEGORIES, 'warning');
                                reject();
                                return;
                            }

                            callAPI({
                                method: 'put',
                                module: 'duty_calculator',
                                component: 'duty_item_category',
                                getAccessTokenSilently,
                                email: userEmail,
                                obj: newRow,
                                onInit: () => setLoadingState((prev) => ({ ...prev, circularLoading: true })),
                                onFinal: () => setLoadingState((prev) => ({ ...prev, circularLoading: false })),
                                onSuccess: (resp) => {
                                    dispatch((d, getState) => {
                                        const prev = getState().itemCategories;
                                        d(
                                            setItemCategories(
                                                prev.map((row) => (row.id === newRow.id ? resp.data : row)),
                                            ),
                                        );
                                    });
                                    showSnackbar(dispatch, MESSAGES.UPDATE_SUCCESS, 'success');
                                    resolve();
                                },
                                onError: () => {
                                    showSnackbar(dispatch, MESSAGES.UPDATE_ERROR, 'error');
                                    reject();
                                },
                            });
                        });
                        return newRow;
                    } catch (error) {
                        throw error;
                    }
                }}
                onProcessRowUpdateError={(error) => {
                    console.error('Row update failed:', error);
                }}
                sx={{ width: '500px' }}
            />
        </Box>
    );
}
